import React, { useState, useEffect, useCallback } from 'react';
import { Save, Globe, Loader2, X, Trash2, AlertTriangle, Settings, Download, CheckCircle2, Workflow, RotateCw, Cpu } from 'lucide-react';
import baseapi from '../../services/baseapi';
import integrationapi from '../../services/integrationapi';
import { showError, showSuccess } from '../../utils/toast';
import { hasPermission } from '../../utils/permissionsHelper';
import { useClientLevel } from '../../hooks/useClientLevel';
import ExternalApiModal from './ExternalApiModal';
import ExternalApiSettings from './ExternalApiSettings';
import { WhatsAppIntegrationModal } from './WhatsAppIntegrationModal';

export default function ApiIntegration() {
    const level = useClientLevel();
    const [integrations, setIntegrations] = useState<any[]>([]);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isExternalApiModalOpen, setIsExternalApiModalOpen] = useState(false);
    const [isExternalApiSettingsOpen, setIsExternalApiSettingsOpen] = useState(false);
    const [isExternalApiDeleteModalOpen, setIsExternalApiDeleteModalOpen] = useState(false);
    const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
    const [isWhatsAppDeleteModalOpen, setIsWhatsAppDeleteModalOpen] = useState(false);

    const canCreate = hasPermission('integrations', 'create')
    const canDelete = hasPermission('integrations', 'delete')
    const canUpdate = hasPermission('integrations', 'update')

    const [formData, setFormData] = useState({
        apiKey: '',
        apiUrl: '',
        apiSecret: ''
    });

    const fetchIntegrations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await baseapi.get("/integrations");
            if (response.data.status) {
                setIntegrations(response.data.data || []);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchIntegrations();
    }, [fetchIntegrations]);

    const existingDeskproRaw = integrations.find(item => item.integration_type === 'deskpro');
    const existingDeskpro = existingDeskproRaw ? {
        ...existingDeskproRaw,
        parsedData: typeof existingDeskproRaw.integration_data === 'string'
            ? JSON.parse(existingDeskproRaw.integration_data)
            : existingDeskproRaw.integration_data
    } : null;

    const externalApiIntegration = integrations.find(item =>
        item.integration_type === 'external_api' ||
        item.integration_type === 'external_api_key'
    );

    const existingTeams = integrations.find(item =>
        item.integration_type === 'teams' ||
        item.integration_type === 'microsoft_teams'
    );

    const existingSlack = integrations.find(item =>
        item.integration_type === 'slack'
    );

    const existingSso = integrations.find(item =>
        item.integration_type === 'microsoft-sso' ||
        item.integration_type === 'microsoft_sso' ||
        item.integration_type === 'sso'
    );

    const existingGoogleWorkspace = integrations.find(item =>
        item.integration_type === 'google-workspace' ||
        item.integration_type === 'google_workspace' ||
        item.integration_type === 'google'
    );

    const existingWhatsapp = integrations.find(item =>
        item.integration_type === 'whatsapp'
    );

    // const handleInstall = async (provider: 'teams' | 'slack' | 'microsoft-sso' | 'google-workspace') => {
    //     try {
    //         const displayName =
    //             provider === 'teams' ? 'Microsoft Teams' :
    //                 provider === 'slack' ? 'Slack' :
    //                     provider === 'microsoft-sso' ? 'Microsoft SSO' : 'Google Workspace';

    //         showSuccess(`Initiating ${displayName} authorization...`);
    //         const response = await integrationapi.get(`integrations/${provider}/admin-consent-url`);
    //         if (response.data.status && response.data.admin_consent_url) {
    //             window.location.href = response.data.admin_consent_url;
    //         } else {
    //             showError(`Failed to get consent URL for ${displayName}`);
    //         }
    //     } catch (error: any) {
    //         showError(error.response?.data?.message || `Failed to initiate ${provider} installation`);
    //     }
    // };

    const handleUninstall = async (provider: 'teams' | 'slack' | 'microsoft-sso' | 'google-workspace', integrationId: any) => {
        try {
            setSubmitting(true);
            const displayName =
                provider === 'teams' ? 'Microsoft Teams' :
                    provider === 'slack' ? 'Slack' :
                        provider === 'microsoft-sso' ? 'Microsoft SSO' : 'Google Workspace';

            const response = await integrationapi.delete(`integrations/${provider}/uninstall`);
            if (response.data.status) {
                showSuccess(`${displayName} disconnected successfully`);
                fetchIntegrations();
            } else {
                showError(`Failed to disconnect ${displayName}`);
            }
        } catch (error: any) {
            console.error("Uninstall error:", error);
            try {
                const response = await baseapi.delete(`/integrations/${integrationId}`);
                if (response.data.status) {
                    const displayName =
                        provider === 'teams' ? 'Microsoft Teams' :
                            provider === 'slack' ? 'Slack' :
                                provider === 'microsoft-sso' ? 'Microsoft SSO' : 'Google Workspace';
                    showSuccess(`${displayName} disconnected successfully`);
                    fetchIntegrations();
                    return;
                }
            } catch (e) { }
            showError(error.response?.data?.message || `Failed to remove ${provider} integration`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenEdit = () => {
        if (existingDeskpro) {
            setFormData({
                apiUrl: existingDeskpro.parsedData.api_url || '',
                apiKey: existingDeskpro.parsedData.api_key || '',
                apiSecret: existingDeskpro.parsedData.api_secret || ''
            });
            setIsEditing(true);
            setIsConfigModalOpen(true);
        }
    };

    const handleDelete = async () => {
        if (!existingDeskpro) return;
        try {
            setSubmitting(true);
            const response = await baseapi.delete('/ticketing/integration');
            if (response.data.status) {
                showSuccess("Integration disconnected successfully");
                setIsDeleteModalOpen(false);
                fetchIntegrations();
            }
        } catch (error) {
            showError("Failed to remove integration");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteExternalApi = async () => {
        try {
            setSubmitting(true);
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.delete("integrations/external-api/uninstall", { params });
            if (response.data.status) {
                showSuccess("External API disconnected successfully");
                setIsExternalApiDeleteModalOpen(false);
                fetchIntegrations();
            }
        } catch (error) {
            showError("Failed to remove external API");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteWhatsapp = async () => {
        try {
            setSubmitting(true);
            const response = await baseapi.delete("/whatsapp/integration");
            if (response.data.status || response.status === 200) {
                showSuccess("WhatsApp disconnected successfully");
                setIsWhatsAppDeleteModalOpen(false);
                fetchIntegrations();
            }
        } catch (error) {
            showError("Failed to remove WhatsApp integration");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const params = {
            name: "Deskpro",
            integration_type: "deskpro",
            integration_data: {
                api_url: formData.apiUrl,
                api_key: formData.apiKey,
                api_secret: formData.apiSecret
            },
            is_enabled: true
        };

        try {
            setSubmitting(true);
            const response = isEditing
                ? await baseapi.put("/ticketing/integration", params)
                : await baseapi.post("/ticketing/integration", params);

            if (response.data.status) {
                showSuccess(isEditing ? "Updated successfully" : "Connected successfully");
                setIsConfigModalOpen(false);
                fetchIntegrations();
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "An error occurred");
        } finally {
            setSubmitting(false);
        }
    };

    const handleForceSync = async () => {
        try {
            setSyncing(true);
            const response = await baseapi.get("/ticketing/metadata", { params: { refresh: true } });

            if (response.data.status) {
                showSuccess("Sync completed successfully");
            } else {
                showError("Sync failed to complete");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Failed to sync metadata");
        } finally {
            setSyncing(false);
        }
    };

    if (loading && integrations.length === 0) {
        return <div className="p-20 flex justify-center bg-slate-50 min-h-screen"><Loader2 className="animate-spin text-[#422568]" /></div>;
    }

    return (
        <div className="bg-slate-50 p-3 pb-20 min-h-screen w-full">
            <div className='flex gap-2 items-center mb-4'>
                <div className="bg-[#422568] p-1.5 rounded">
                    <Workflow size={16} className="text-white" />
                </div>
                <div>
                    <h1 className="font-black text-lg text-slate-800 leading-none">Integrations</h1>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-1">Connect External Services</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                {/* ─── RunDeskPro ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/rdp_logo.png" alt="RDP" className="w-full h-full object-contain" />
                        </div>
                        {existingDeskpro && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>

                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">RunDeskPro</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Helpdesk integration.</p>

                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {existingDeskpro ? (
                            <>
                                {canUpdate && (
                                    <button onClick={handleOpenEdit}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-all border border-slate-200/50 active:scale-95'>
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>
                                )}

                                {canDelete && (
                                    <button
                                        onClick={() => setIsDeleteModalOpen(true)}
                                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-transparent border border-transparent text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl text-xs font-bold transition-all active:scale-95"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )}
                            </>
                        ) : (
                            canCreate && (
                                <button
                                    onClick={() => { setFormData({ apiUrl: '', apiKey: '', apiSecret: '' }); setIsEditing(false); setIsConfigModalOpen(true); }}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#422568]/20 hover:bg-[#422568]/30 text-[#422568] border border-[#422568]/35 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer active:scale-95"
                                >
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* ─── External API ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-inner">
                            <Cpu className="text-[#422568]" size={24} />
                        </div>
                        {externalApiIntegration && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>

                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">External API</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Connect external services via secure tokens.</p>

                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {externalApiIntegration ? (
                            <>
                                <button
                                    onClick={() => setIsExternalApiSettingsOpen(true)}
                                    className='flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-all border border-slate-200/50 active:scale-95'
                                >
                                    <Settings className="w-4 h-4" />
                                    Settings
                                </button>
                                {canDelete && (
                                    <button
                                        onClick={() => setIsExternalApiDeleteModalOpen(true)}
                                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-transparent border border-transparent text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl text-xs font-bold transition-all active:scale-95"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )}
                            </>
                        ) : (
                            canCreate && (
                                <button
                                    onClick={() => setIsExternalApiModalOpen(true)}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#422568]/20 hover:bg-[#422568]/30 text-[#422568] border border-[#422568]/35 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer active:scale-95"
                                >
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-305 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/whatsapp.svg" alt="Whatsapp" className="w-full h-full object-contain" />
                        </div>
                        {existingWhatsapp && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Whatsapp</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Whatsapp integration</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {existingWhatsapp ? (
                            <>
                                {canUpdate && (
                                    <button
                                        onClick={() => setIsWhatsAppModalOpen(true)}
                                        className='flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-all border border-slate-200/50 active:scale-95'
                                    >
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </button>
                                )}

                                {canDelete && (
                                    <button
                                        onClick={() => setIsWhatsAppDeleteModalOpen(true)}
                                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-transparent border border-transparent text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl text-xs font-bold transition-all active:scale-95"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Uninstall
                                    </button>
                                )}
                            </>
                        ) : (
                            canCreate && (
                                <button
                                    onClick={() => setIsWhatsAppModalOpen(true)}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#422568]/20 hover:bg-[#422568]/30 text-[#422568] border border-[#422568]/35 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer active:scale-95"
                                >
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* ─── Microsoft Teams ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/teams.svg" alt="Teams" className="w-full h-full object-contain" />
                        </div>
                        {existingTeams && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Microsoft Teams</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Organization-wide Teams integration.</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {existingTeams ? (
                            canDelete && (
                                <button
                                    onClick={() => handleUninstall('teams', existingTeams.id)}
                                    className="w-full flex items-center justify-center gap-2 py-2 bg-transparent border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-xl text-xs font-bold transition-all active:scale-95"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Uninstall
                                </button>
                            )
                        ) : (
                            canCreate && (
                                <button disabled className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-450 border border-slate-200 text-xs font-black uppercase tracking-wider rounded-xl opacity-60 cursor-not-allowed">
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                

                {/* ─── Microsoft SSO ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/microsoftgraph.svg" alt="Microsoft SSO" className="w-full h-full object-contain" />
                        </div>
                        {existingSso && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Microsoft SSO</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Enable Single Sign-On with Microsoft for your organization.</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {existingSso ? (
                            canDelete && (
                                <button
                                    onClick={() => handleUninstall('microsoft-sso', existingSso.id)}
                                    className="w-full flex items-center justify-center gap-2 py-2 bg-transparent border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-xl text-xs font-bold transition-all active:scale-95"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Uninstall
                                </button>
                            )
                        ) : (
                            canCreate && (
                                <button disabled className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-450 border border-slate-200 text-xs font-black uppercase tracking-wider rounded-xl opacity-60 cursor-not-allowed">
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* ─── Slack ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/slack.svg" alt="Slack" className="w-full h-full object-contain" />
                        </div>
                        {existingSlack && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Slack</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Slack workspace integration.</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {existingSlack ? (
                            canDelete && (
                                <button
                                    onClick={() => handleUninstall('slack', existingSlack.id)}
                                    className="w-full flex items-center justify-center gap-2 py-2 bg-transparent border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-xl text-xs font-bold transition-all active:scale-95"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Uninstall
                                </button>
                            )
                        ) : (
                            canCreate && (
                                <button disabled className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-450 border border-slate-200 text-xs font-black uppercase tracking-wider rounded-xl opacity-60 cursor-not-allowed">
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* ─── Google Workspace ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-300 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/google.svg" alt="Google Workspace" className="w-full h-full object-contain" />
                        </div>
                        {existingGoogleWorkspace && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-600 text-[10px] font-black uppercase tracking-wide shadow-sm">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Installed
                            </div>
                        )}
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Google Workspace</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Enable Google Single Sign-On and user directory synchronization.</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {existingGoogleWorkspace ? (
                            canDelete && (
                                <button
                                    onClick={() => handleUninstall('google-workspace', existingGoogleWorkspace.id)}
                                    className="w-full flex items-center justify-center gap-2 py-2 bg-transparent border border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-350 rounded-xl text-xs font-bold transition-all active:scale-95"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Uninstall
                                </button>
                            )
                        ) : (
                            canCreate && (
                                <button disabled className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-450 border border-slate-200 text-xs font-black uppercase tracking-wider rounded-xl opacity-60 cursor-not-allowed">
                                    <Download className="w-4 h-4" />
                                    Install
                                </button>
                            )
                        )}
                    </div>
                </div>

                {/* ─── Webhook ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-305 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/webhook.svg" alt="Webhook" className="w-full h-full object-contain" />
                        </div>
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Webhook</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Webhook integration</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {canCreate && (
                            <button disabled className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-450 border border-slate-200 text-xs font-black uppercase tracking-wider rounded-xl opacity-60 cursor-not-allowed">
                                <Download className="w-4 h-4" />
                                Install
                            </button>
                        )}
                    </div>
                </div>

                {/* ─── Email ─── */}
                <div className="bg-white border border-slate-200 hover:border-[#422568]/35 rounded-2xl p-6 flex flex-col h-full shadow-lg shadow-slate-100/50 hover:shadow-xl transition-all duration-305 relative group">
                    <div className="flex justify-between items-start mb-6">
                        <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl p-1 shadow-inner border border-slate-100">
                            <img src="/email.png" alt="Email" className="w-full h-full object-contain" />
                        </div>
                    </div>
                    <h2 className="text-[16px] font-black text-slate-800 mb-1 leading-tight capitalize">Email</h2>
                    <p className="text-[12px] text-slate-500 font-semibold mb-6 flex-grow leading-relaxed">Email integration</p>
                    <div className='flex gap-3 w-full mt-auto pt-5 border-t border-slate-100'>
                        {canCreate && (
                            <button disabled className="w-full flex items-center justify-center gap-2 py-2.5 bg-slate-100 text-slate-450 border border-slate-200 text-xs font-black uppercase tracking-wider rounded-xl opacity-60 cursor-not-allowed">
                                <Download className="w-4 h-4" />
                                Install
                            </button>
                        )}
                    </div>
                </div>



            </div>

            {/* ─── Configuration Modal (Add/Edit) ─── */}
            {isConfigModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-xl shadow-2xl animate-in zoom-in duration-300">
                        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-t-2xl">
                            <h2 className="text-lg font-black text-slate-800">{isEditing ? 'Update Credentials' : 'Configure RunDeskPro'}</h2>
                            <button onClick={() => setIsConfigModalOpen(false)} className="text-slate-400 hover:text-slate-800 p-1 hover:bg-slate-100 rounded-full transition-colors"><X className="w-5 h-5" /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 space-y-6 bg-white rounded-b-2xl">
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Base API URL</label>
                                    <div className="relative group">
                                        <Globe className="absolute left-3 top-3.5 h-4 w-4 text-slate-450 group-focus-within:text-[#422568] transition-colors" />
                                        <input name="apiUrl" type="url" value={formData.apiUrl} onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })} className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:ring-4 focus:ring-[#422568]/10 focus:border-[#422568] rounded-xl pl-10 pr-4 py-3 text-sm text-slate-800 font-semibold outline-none transition-all" placeholder="https://api.rundeskpro.com/v1" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="group">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">API Key</label>
                                        <input name="apiKey" value={formData.apiKey} onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })} autoComplete="one-time-code" className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:ring-4 focus:ring-[#422568]/10 focus:border-[#422568] rounded-xl px-4 py-3 text-sm text-slate-800 font-semibold outline-none transition-all" />
                                    </div>
                                    <div className="group">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">API Secret</label>
                                        <input name="apiSecret" type="password" value={formData.apiSecret} onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })} autoComplete="new-password" placeholder="••••••••" className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:ring-4 focus:ring-[#422568]/10 focus:border-[#422568] rounded-xl px-4 py-3 text-sm text-slate-800 font-semibold outline-none transition-all" />
                                    </div>
                                </div>
                            </div>

                            {/* Fixed Action Button Layout */}
                            <div className="pt-4 flex justify-between items-center gap-4 w-full border-t border-slate-100">
                                <button type="button" onClick={() => setIsConfigModalOpen(false)} className="px-6 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-550 hover:bg-slate-50 transition-all active:scale-95">
                                    Cancel
                                </button>

                                <div className="flex items-center gap-3">
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={handleForceSync}
                                            disabled={syncing || submitting}
                                            className="px-6 py-3 border border-[#422568]/50 text-[#422568] rounded-xl text-sm font-bold hover:bg-[#422568]/50 transition-all active:scale-95 flex justify-center items-center gap-2"
                                        >
                                            <RotateCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                                            Force Sync
                                        </button>
                                    )}

                                    <button type="submit" disabled={submitting || syncing} className="px-6 py-3 bg-[#422568] hover:bg-[#422568] text-white rounded-xl text-sm font-bold transition-all active:scale-95 flex justify-center items-center gap-2">
                                        {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                                        {isEditing ? 'Save Changes' : 'Connect Service'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ─── Delete Confirmation Modal ─── */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden text-slate-800">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-100 shadow-sm animate-pulse">
                                <AlertTriangle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h2 className="text-xl font-black text-slate-800 mb-2 uppercase tracking-tight">Disconnect Integration?</h2>
                            <p className="text-sm text-slate-500 font-semibold leading-relaxed">
                                Are you sure you want to remove <span className="text-slate-850 font-bold">RunDeskPro</span>? This will stop all synchronization immediately.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={submitting}
                                className="flex-1 py-3 bg-[#422568] hover:bg-[#422568] text-white rounded-xl text-sm font-black uppercase tracking-wider transition-all shadow-lg shadow-[#422568] active:scale-95 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── External API Delete Confirmation Modal ─── */}
            {isExternalApiDeleteModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden text-slate-800">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-100 shadow-sm animate-pulse">
                                <AlertTriangle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h2 className="text-xl font-black text-slate-800 mb-2 uppercase tracking-tight">Disconnect API?</h2>
                            <p className="text-sm text-slate-500 font-semibold leading-relaxed">
                                Are you sure you want to remove <span className="text-slate-850 font-bold">External API</span>? All services using these credentials will lose access.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                            <button
                                onClick={() => setIsExternalApiDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteExternalApi}
                                disabled={submitting}
                                className="flex-1 py-3 bg-[#422568] hover:bg-[#422568] text-white rounded-xl text-sm font-black uppercase tracking-wider transition-all shadow-lg shadow-[#422568] active:scale-95 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── WhatsApp Delete Confirmation Modal ─── */}
            {isWhatsAppDeleteModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden text-slate-800">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-100 shadow-sm animate-pulse">
                                <AlertTriangle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h2 className="text-xl font-black text-slate-800 mb-2 uppercase tracking-tight">Disconnect WhatsApp?</h2>
                            <p className="text-sm text-slate-500 font-semibold leading-relaxed">
                                Are you sure you want to remove <span className="text-slate-850 font-bold">WhatsApp</span>? All alert routing to this number will stop immediately.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                            <button
                                onClick={() => setIsWhatsAppDeleteModalOpen(false)}
                                className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteWhatsapp}
                                disabled={submitting}
                                className="flex-1 py-3 bg-[#422568] hover:bg-[#422568] text-white rounded-xl text-sm font-black uppercase tracking-wider transition-all shadow-lg shadow-[#422568] active:scale-95 flex justify-center items-center gap-2"
                            >
                                {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : 'Yes, Remove'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ExternalApiModal
                isOpen={isExternalApiModalOpen}
                onClose={() => setIsExternalApiModalOpen(false)}
                onSuccess={() => fetchIntegrations()}
            />
            <ExternalApiSettings
                isOpen={isExternalApiSettingsOpen}
                onClose={() => setIsExternalApiSettingsOpen(false)}
                onUninstall={() => fetchIntegrations()}
                integrationData={externalApiIntegration}
            />
            <WhatsAppIntegrationModal
                isOpen={isWhatsAppModalOpen}
                onClose={() => setIsWhatsAppModalOpen(false)}
                onSuccess={() => fetchIntegrations()}
            />
        </div>
    );
}